import type { FC } from 'react';
import { Url } from '@wix/da-url';
import { PlaygroundSubsection, StudioSection } from '../../types/store';
import SearchBlankSlate from './sections/SearchBlankSlate';
import ProductsBlankSlate from './sections/ProductsBlankSlate';
import LiteratureBlankSlate from './sections/LiteratureBlankSlate';
import ScheduledBlankSlate from './sections/ScheduledBlankSlate';
import PublishedBlankSlate from './sections/PublishedBlankSlate';
import StashBlankSlate from './sections/StashBlankSlate';
import JournalsBlankSlate from './sections/JournalsBlankSlate';
import ShopReportsBlankSlate from './sections/ShopReportsBlankSlate';
import DraftsBlankSlate from './sections/DraftsBlankSlate';
import SubscriptionsBlankSlate from './sections/SubscriptionsBlankSlate';
import TierBlankSlate from './sections/TierBlankSlate';
import WixelEmptyState from '../playground/emptyStateVariants/WixelEmptyState';
import DreamupEmptyState from '../playground/emptyStateVariants/DreamupEmptyState';
import DrawEmptyState from '../playground/emptyStateVariants/DrawEmptyState';
import AppsBlankSlate from './sections/AppsBlankSlate';

export function illustrationUrl(name: string, cacheVersion = 0): string {
  const path = `eclipse/global/images/${name}`;
  return cacheVersion ? Url.staticCDNLink(`${path}?v=${cacheVersion}`) : Url.staticCDNLink(path);
}

export interface Props {
  activeSection: StudioSection;
  activePlaygroundSection?: PlaygroundSubsection;
  isProductsSection?: boolean;
  isSearchPage?: boolean;
  selectedTierId?: number;
}

const StudioBlankSlate: FC<Props> = ({
  activeSection,
  activePlaygroundSection,
  isProductsSection,
  isSearchPage,
  selectedTierId,
}) => {
  if (isSearchPage) {
    return <SearchBlankSlate />;
  }

  if (isProductsSection) {
    return <ProductsBlankSlate activeSection={activeSection} />;
  }

  if (activeSection === StudioSection.PLAYGROUND) {
    switch (activePlaygroundSection) {
      case PlaygroundSubsection.Draw:
        return <DrawEmptyState />;
      case PlaygroundSubsection.DreamUp:
        return <DreamupEmptyState />;
      case PlaygroundSubsection.Wixel:
        return <WixelEmptyState />;
      default:
        break;
    }
  }

  if (activeSection === StudioSection.SCHEDULED) {
    return <ScheduledBlankSlate />;
  }

  if (activeSection === StudioSection.LITERATURE) {
    return <LiteratureBlankSlate />;
  }

  if (activeSection === StudioSection.PUBLISHED) {
    if (selectedTierId) {
      return <TierBlankSlate />;
    }
    return <PublishedBlankSlate />;
  }

  if (activeSection === StudioSection.PUBLISHED_TIERS) {
    return <SubscriptionsBlankSlate />;
  }

  if (activeSection === StudioSection.STASH) {
    return <StashBlankSlate />;
  }

  if (activeSection === StudioSection.JOURNALS) {
    return <JournalsBlankSlate />;
  }

  if (activeSection === StudioSection.SHOP_REPORTS) {
    return <ShopReportsBlankSlate />;
  }

  if (activeSection === StudioSection.APPS || activeSection === StudioSection.APPS_UNLISTED) {
    return <AppsBlankSlate />;
  }

  return <DraftsBlankSlate />;
};
StudioBlankSlate.displayName = 'StudioBlankSlate';

export default StudioBlankSlate;
